<script setup>
// Assistente de IA — quanto o módulo consumiu e onde.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import assistenteIaApi from 'dashboard/api/assistenteIa';
import { useAssistenteIaApi } from '../composables/useAssistenteIaApi';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TabelaVazia from '../components/TabelaVazia.vue';

const { t } = useI18n();
const { carregando, erro, chamar } = useAssistenteIaApi();

const dados = ref(null);
const meta = ref({ count: 0, current_page: 1 });
const assistentes = ref([]);
const filtros = ref({ de: '', ate: '', assistenteId: '', tipo: '', page: 1 });

// O fuso que o SERVIDOR usou para cortar os dias. Ele vem no `meta` da resposta.
//
// Esta tela misturava tres fusos (AIA-14, A3): os botoes "7/30 dias" usavam o do navegador, o
// periodo padrao usava o da conta, e a hora de cada chamada saia em `toLocaleString()` do
// navegador. Quem opera de outro fuso lia tres verdades diferentes na mesma tela.
const fuso = ref(null);

// O dia (AAAA-MM-DD) de uma data NO FUSO da conta. `toISOString()` converte para UTC antes de
// cortar: depois das 21h em Sao Paulo ele ja devolve o dia seguinte (AIA-13, A3).
const diaNoFuso = data => {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: fuso.value || undefined,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(data);

  return partes;
};

// Enquanto o fuso da conta nao chegou — primeira carga, ou o consumo falhou —, os botoes de
// periodo ficam DESABILITADOS (AIA-15, A1). A alternativa seria calcular os dias no fuso do
// navegador em silencio, e foi justamente isso que o A3 da AIA-14 foi corrigir: quem opera de
// outro fuso pediria um intervalo e leria outro, sem saber.
const semFuso = computed(() => !fuso.value);

const emDias = dias => {
  if (!fuso.value) return;

  const hoje = new Date();
  const inicio = new Date(hoje.getTime() - dias * 24 * 60 * 60 * 1000);

  filtros.value.de = diaNoFuso(inicio);
  filtros.value.ate = diaNoFuso(hoje);
  filtros.value.page = 1;
  carregar();
};

const carregar = async () => {
  const resposta = await chamar(assistenteIaApi.consumo(filtros.value));
  if (!resposta) return;

  dados.value = resposta.payload;
  meta.value = resposta.meta;
  if (resposta.meta?.fuso) fuso.value = resposta.meta.fuso;
};

const nomeDoTipo = tipo =>
  tipo ? t(`ASSISTENTE_IA.CONSUMO.TIPOS.${tipo.toUpperCase()}`) : '—';

const quando = data =>
  new Date(data).toLocaleString(undefined, {
    timeZone: fuso.value || undefined,
  });

onMounted(async () => {
  const lista = await chamar(assistenteIaApi.assistentes());
  if (lista) assistentes.value = lista.payload;
  carregar();
});
</script>

<template>
  <section class="flex flex-col gap-4">
    <Banner v-if="erro" color="ruby">{{ erro }}</Banner>

    <div>
      <h2 class="text-heading-2 text-n-slate-12">
        {{ t('ASSISTENTE_IA.CONSUMO.TITULO') }}
      </h2>
      <p class="mb-0 text-n-slate-11">{{ t('ASSISTENTE_IA.CONSUMO.DESCRICAO') }}</p>
    </div>

    <div class="flex flex-wrap items-end gap-3">
      <Button
        :label="t('ASSISTENTE_IA.CONSUMO.SETE_DIAS')"
        variant="faded"
        color="slate"
        size="xs"
        :disabled="semFuso"
        @click="emDias(7)"
      />
      <Button
        :label="t('ASSISTENTE_IA.CONSUMO.TRINTA_DIAS')"
        variant="faded"
        color="slate"
        size="xs"
        :disabled="semFuso"
        @click="emDias(30)"
      />

      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('ASSISTENTE_IA.CONSUMO.DE') }}
        <input
          v-model="filtros.de"
          type="date"
          class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('ASSISTENTE_IA.CONSUMO.ATE') }}
        <input
          v-model="filtros.ate"
          type="date"
          class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2"
        />
      </label>

      <select
        v-model="filtros.assistenteId"
        class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2"
      >
        <option value="">{{ t('ASSISTENTE_IA.ATENDIMENTOS.TODOS') }}</option>
        <option v-for="a in assistentes" :key="a.id" :value="a.id">{{ a.nome }}</option>
      </select>

      <Button
        :label="t('ASSISTENTE_IA.COMUM.ATUALIZAR')"
        size="xs"
        :is-loading="carregando"
        @click="
          filtros.page = 1;
          carregar();
        "
      />
    </div>

    <div v-if="carregando && !dados" class="flex justify-center py-16">
      <Spinner />
    </div>

    <template v-else-if="dados">
      <div class="grid gap-3 sm:grid-cols-4">
        <div
          v-for="cartao in [
            { rotulo: t('ASSISTENTE_IA.CONSUMO.CHAMADAS'), valor: dados.totais.chamadas },
            { rotulo: t('ASSISTENTE_IA.CONSUMO.FALHAS'), valor: dados.totais.falhas },
            {
              rotulo: t('ASSISTENTE_IA.CONSUMO.TOKENS_ENTRADA'),
              valor: dados.totais.tokens_entrada,
            },
            {
              rotulo: t('ASSISTENTE_IA.CONSUMO.TOKENS_SAIDA'),
              valor: dados.totais.tokens_saida,
            },
          ]"
          :key="cartao.rotulo"
          class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <span class="text-xs text-n-slate-11">{{ cartao.rotulo }}</span>
          <span class="text-heading-2 text-n-slate-12">{{ cartao.valor }}</span>
        </div>
      </div>

      <div class="grid gap-4 sm:grid-cols-3">
        <div
          v-for="grupo in [
            {
              titulo: t('ASSISTENTE_IA.CONSUMO.POR_ASSISTENTE'),
              linhas: dados.por_assistente.map(l => ({
                nome: l.assistente?.nome || t('ASSISTENTE_IA.CONSUMO.SEM_ASSISTENTE'),
                chamadas: l.chamadas,
              })),
            },
            {
              titulo: t('ASSISTENTE_IA.CONSUMO.POR_TIPO'),
              linhas: dados.por_tipo.map(l => ({
                nome: nomeDoTipo(l.tipo),
                chamadas: l.chamadas,
              })),
            },
            {
              titulo: t('ASSISTENTE_IA.CONSUMO.POR_PROVEDOR'),
              linhas: dados.por_provedor.map(l => ({
                nome: l.provedor || '—',
                chamadas: l.chamadas,
              })),
            },
          ]"
          :key="grupo.titulo"
          class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak"
        >
          <h3 class="text-sm font-medium text-n-slate-12">{{ grupo.titulo }}</h3>
          <div
            v-for="linha in grupo.linhas"
            :key="linha.nome"
            class="flex justify-between text-sm text-n-slate-11"
          >
            <span>{{ linha.nome }}</span>
            <span>{{ linha.chamadas }}</span>
          </div>
        </div>
      </div>

      <TabelaVazia
        v-if="!dados.chamadas.length"
        :mensagem="t('ASSISTENTE_IA.CONSUMO.VAZIO')"
      />

      <table v-else class="w-full text-sm text-left">
        <thead class="border-b text-n-slate-11 border-n-weak">
          <tr>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CONSUMO.QUANDO') }}</th>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CONSUMO.TIPO') }}</th>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CONSUMO.MODELO') }}</th>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CONSUMO.TOKENS_ENTRADA') }}</th>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CONSUMO.TOKENS_SAIDA') }}</th>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CONSUMO.LATENCIA') }}</th>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CONSUMO.SUCESSO') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="chamada in dados.chamadas" :key="chamada.id" class="border-b border-n-weak">
            <td class="py-2 text-n-slate-11">{{ quando(chamada.created_at) }}</td>
            <td class="py-2 text-n-slate-11">
              {{ nomeDoTipo(chamada.tipo) }}
              <span v-if="chamada.falso" class="text-xs text-n-amber-11">
                ({{ t('ASSISTENTE_IA.CONSUMO.TESTE') }})
              </span>
            </td>
            <td class="py-2 text-n-slate-11">{{ chamada.modelo }}</td>
            <td class="py-2 text-n-slate-11">{{ chamada.tokens_entrada }}</td>
            <td class="py-2 text-n-slate-11">{{ chamada.tokens_saida }}</td>
            <td class="py-2 text-n-slate-11">{{ chamada.latencia_ms }} ms</td>
            <td class="py-2">
              <span v-if="chamada.sucesso" class="text-n-teal-11">✓</span>
              <span v-else class="text-n-ruby-11" :title="chamada.erro">✕</span>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="meta.count > dados.chamadas.length" class="flex items-center gap-2">
        <Button
          :label="t('ASSISTENTE_IA.COMUM.ANTERIOR')"
          variant="faded"
          color="slate"
          size="xs"
          :disabled="filtros.page <= 1"
          @click="
            filtros.page -= 1;
            carregar();
          "
        />
        <span class="text-sm text-n-slate-11">
          {{ t('ASSISTENTE_IA.COMUM.PAGINA') }} {{ meta.current_page }}
        </span>
        <Button
          :label="t('ASSISTENTE_IA.COMUM.PROXIMA')"
          variant="faded"
          color="slate"
          size="xs"
          @click="
            filtros.page += 1;
            carregar();
          "
        />
      </div>
    </template>
  </section>
</template>
